import React, { useState, useEffect, useContext } from "react"
import ClientTransactions from "./ClientTransactions"
import useLoading from "../../../../hooks/useLoading"
import UserTransactions from "./UserTransactions"
import { Button } from "../../../../components/ui/button"
import { ClientPaymentRequests } from "../../../../api/app.service"
import { UserCtx } from "../../../../context/userContext"

interface Props {
  localPaymentMethods: any[]
}

const TransactionSection: React.FC<Props> = ({ localPaymentMethods }) => {
  const { user } = useContext(UserCtx)
  const { startLoading, stopLoading } = useLoading()
  const [mainTab, setMainTab] = useState<"user" | "client">("user")
  const [subTab, setSubTab] = useState<"past" | "upcoming">("upcoming")
  const [userTransactions, setUserTransactions] = useState<any[]>([])
  const [refreshKey, setRefreshKey] = useState(0)
  const [clientTransactions, setClientTransactions] = useState<any[]>([])
  const getUserTransactions = async () => {
    startLoading()
    try {
      const data = await ClientPaymentRequests.getCoachPayments({
        user_id: parseInt(user.user_id),
      })

      const subxList = data?.subx_payments_list || []
      const coachList = data?.coach_purchase_list || []

      const normalized = [
        ...subxList.map((item) => ({
          date: item.date_processed || item.date_scheduled,
          date_processed: item.date_processed,
          date_scheduled: item.date_scheduled,
          description: item.paypal_promo_code
            ? `Subscription (${item.paypal_promo_code})`
            : "Subscription Payment",
          merchant: "Zoee",
          paymentMethod: item.payment_method || "—",
          amount: item.amount || 0,
          status: item.payment_status?.trim().toLowerCase(),
          service_payment_id: item.service_payment_id,
          service_details: null,
        })),
        ...coachList.map((item) => {
          const parsedDetails =
            typeof item.service_details === "string"
              ? JSON.parse(item.service_details)
              : item.service_details

          return {
            date: item.date_processed || item.date_scheduled,
            date_processed: item.date_processed,
            date_scheduled: item.date_scheduled,
            description: parsedDetails?.title || "Coach Service",
            merchant: item.coach_name || "Coach",
            merchantUserID: item.coach_user_id,
            merchantProfileID: item.coach_profile_id,
            paymentMethod: item.payment_method || "—",
            amount: item.amount || 0,
            status: item.payment_status?.trim().toLowerCase(),
            service_payment_id: item.service_payment_id,
            service_details: parsedDetails || {},
            merchantID: item.paypal_merchant_id,
          }
        }),
      ]

      setUserTransactions([...normalized])
      setRefreshKey((prev) => prev + 1)
      return normalized
    } catch (err) {
      console.error("Error fetching transactions:", err)
      return []
    } finally {
      stopLoading()
    }
  }

  const getClientTransactions = () => {
    startLoading()
    ClientPaymentRequests.getPaymentsClient({ user_id: parseInt(user.user_id) })
      .then((data) => {
        setClientTransactions(data?.client_payments_list || [])
      })
      .finally(stopLoading)
  }

  useEffect(() => {
    getUserTransactions()
    if (user?.isCoach) getClientTransactions()
  }, [])

  return (
    <div className="border border-grayMist p-6 w-full bg-white rounded-lg shadow-sm">
      {user?.isCoach ? (
        <>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <Button
              variant={mainTab === "user" ? "default" : "outline"}
              className={`py-6 text-lg font-semibold border-grayMist text-center leading-snug whitespace-normal break-words ${
                mainTab === "user"
                  ? "shadow-md scale-[1.02]"
                  : "hover:bg-gray-100"
              }`}
              onClick={() => setMainTab("user")}
            >
              My Payments
            </Button>

            <Button
              variant={mainTab === "client" ? "default" : "outline"}
              className={`py-6 text-lg font-semibold border-grayMist text-center leading-snug whitespace-normal break-words ${
                mainTab === "client"
                  ? "shadow-md scale-[1.02]"
                  : "hover:bg-gray-100"
              }`}
              onClick={() => setMainTab("client")}
            >
              Client Payments
            </Button>
          </div>

          {mainTab === "client" ? (
            <ClientTransactions transactions={clientTransactions} user={user} />
          ) : (
            <UserTransactions
              key={refreshKey}
              transactions={userTransactions}
              type={subTab}
              user={user}
              setSubTab={setSubTab}
              localPaymentMethods={localPaymentMethods}
            />
          )}
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 mb-6 w-full">
            {" "}
            <Button
              variant={subTab === "past" ? "default" : "outline"}
              className={`py-6 text-lg font-semibold border-grayMist whitespace-normal break-words text-center leading-snug ${
                subTab === "past"
                  ? "shadow-md scale-[1.02]"
                  : "hover:bg-gray-100"
              }`}
              onClick={() => setSubTab("past")}
            >
              My Past Transactions
            </Button>
            <Button
              variant={subTab === "upcoming" ? "default" : "outline"}
              className={`py-6 text-lg font-semibold border-grayMist whitespace-normal break-words text-center leading-snug ${
                subTab === "upcoming"
                  ? "shadow-md scale-[1.02]"
                  : "hover:bg-gray-100"
              }`}
              onClick={() => setSubTab("upcoming")}
            >
              My Upcoming Transactions
            </Button>
          </div>

          <UserTransactions
            key={refreshKey}
            transactions={userTransactions}
            type={subTab}
            user={user}
            setSubTab={setSubTab}
            localPaymentMethods={localPaymentMethods}
          />
        </>
      )}
    </div>
  )
}

export default TransactionSection
